import React, { useRef } from 'react';
import { ArrowRight } from 'lucide-react';
import { PageHero, FadeIn } from '../components/Shared';
import AuroraOrbsBg from '../components/bg/AuroraOrbsBg';

const PROJECTS = [
    {
        id: 1,
        name: 'The Green Kitchen',
        industry: 'Restaurant',
        desc: 'Full 8-page website with online booking and menu. 35% increase in reservations.',
        gradient: 'from-emerald-200 via-teal-100 to-cyan-200',
        accent: '#00B89E',
    },
    {
        id: 2,
        name: 'BrightSmile Dental',
        industry: 'Healthcare',
        desc: 'Professional site with patient portal. 50% more online bookings.',
        gradient: 'from-sky-200 via-cyan-100 to-indigo-200',
        accent: '#3B82F6',
    },
    {
        id: 3,
        name: 'Swift Estates',
        industry: 'Property',
        desc: 'Property listing site with search filters. 40% more enquiries.',
        gradient: 'from-violet-200 via-purple-100 to-fuchsia-200',
        accent: '#7B61FF',
    },
    {
        id: 4,
        name: 'FitZone PT',
        industry: 'Fitness',
        desc: 'Personal trainer site with booking system. Fully mobile-optimised.',
        gradient: 'from-orange-200 via-pink-100 to-rose-200',
        accent: '#F97316',
    },
    {
        id: 5,
        name: 'Clarke & Partners',
        industry: 'Legal',
        desc: 'Corporate site with blog and client resources section.',
        gradient: 'from-blue-200 via-indigo-100 to-violet-200',
        accent: '#4F46E5',
    },
    {
        id: 6,
        name: 'PetCare UK',
        industry: 'E-commerce',
        desc: 'Online shop with 200+ products. WooCommerce integration.',
        gradient: 'from-yellow-200 via-orange-100 to-red-200',
        accent: '#EAB308',
    },
];

const TiltCard = ({ p }) => {
    const ref = useRef(null);
    const onMove = (e) => {
        const el = ref.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const nx = (e.clientX - rect.left) / rect.width - 0.5;
        const ny = (e.clientY - rect.top) / rect.height - 0.5;
        el.style.transform = `perspective(1000px) rotateX(${-ny * 8}deg) rotateY(${nx * 10}deg) translateZ(8px)`;
    };
    const onLeave = () => {
        const el = ref.current;
        if (!el) return;
        el.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) translateZ(0)';
    };
    return (
        <article
            ref={ref}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            className="tilt-card group block bg-white border border-[#E8E8EC] rounded-3xl hover:shadow-card-hover transition-shadow duration-500 overflow-hidden"
            data-testid={`portfolio-card-${p.id}`}
        >
            <div className={`relative aspect-[4/3] bg-gradient-to-br ${p.gradient} overflow-hidden`}>
                <div className="absolute inset-0 grid-pattern-light opacity-50" />
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center px-6 tilt-inner">
                        <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#1A1A2E]/60">
                            Case Study
                        </div>
                        <div className="mt-2 font-display text-2xl font-semibold tracking-tight text-[#1A1A2E]/90">
                            {p.name}
                        </div>
                    </div>
                </div>
                <div className="absolute top-4 left-4 px-2.5 py-1 bg-white/85 backdrop-blur-md border border-white/60 rounded-full text-[11px] font-semibold tracking-wide text-[#1A1A2E]">
                    {p.industry}
                </div>
            </div>
            <div className="p-6 tilt-inner">
                <h3 className="font-display text-[18px] font-semibold tracking-tight text-[#1A1A2E]">
                    {p.name}
                </h3>
                <p className="mt-2 text-[14px] text-[#4A4A5A] leading-relaxed">
                    {p.desc}
                </p>
                <button
                    type="button"
                    className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#7B61FF] group-hover:gap-2.5 transition-all"
                    data-testid={`portfolio-link-${p.id}`}
                >
                    View Case Study <ArrowRight className="w-4 h-4" />
                </button>
            </div>
        </article>
    );
};

const Portfolio = () => {
    return (
        <main className="bg-white text-[#1A1A2E] pb-24" data-testid="portfolio-page">
            <PageHero
                label="Selected work"
                title="Our work."
                subtitle="Real results for real businesses across the UK."
                testId="portfolio-hero"
                bg={<AuroraOrbsBg />}
            />

            <section className="py-12 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" style={{ perspective: '1000px' }}>
                        {PROJECTS.map((p, i) => (
                            <FadeIn key={p.id} delay={(i % 3) * 0.1}>
                                <TiltCard p={p} />
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>
        </main>
    );
};

export default Portfolio;
